import { useAuth0 } from "@auth0/auth0-react";
import type { MFEDependencies } from "@pepperstone/mfe-dev-kit/types";
import type { LocaleCodeType } from "@pepperstone/pepper-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { clearAccessToken } from "../src/api";
import { LoadingScreen } from "../src/components/loading-screen/LoadingScreen";
import { useEmbeddedLayout } from "../src/hooks/useEmbeddedLayout";
import { useSessionResumeGuard } from "../src/hooks/useSessionResumeGuard";
import { render } from "../src/main";
import { ContainerLayout } from "./ContainerLayout";
import { ContainerLogin } from "./ContainerLogin";
import { getDependenciesSimple } from "./getDependenciesSimple";

export const ContainerApp = () => {
  const auth0 = useAuth0();
  const [dependencies, setDependencies] = useState<MFEDependencies>();
  const isEmbeddedLayout = useEmbeddedLayout(); // Temporary flag for mobile POC
  const hasRendered = useRef(false);

  useEffect(() => {
    if (auth0.isAuthenticated && !auth0.isLoading && !dependencies) {
      console.log("User is authenticated, fetching dependencies...");
      setDependencies(getDependenciesSimple(auth0));
    }
  }, [auth0, auth0.isAuthenticated, auth0.isLoading, dependencies]);

  // Re-validate the Auth0 session when the page is restored from bfcache or
  // re-shown, so a stale in-memory token can't outlive a logout (see hook docs).
  const verifySession = useCallback(async () => {
    try {
      await auth0.getAccessTokenSilently();
      return true;
    } catch (error) {
      const code = (error as { error?: string })?.error;
      // Only treat definitive auth errors as logged-out; ignore transient ones.
      return !(
        code === "login_required" ||
        code === "missing_refresh_token" ||
        code === "invalid_grant"
      );
    }
  }, [auth0]);

  const invalidateSession = useCallback(() => {
    clearAccessToken();
    auth0.logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0]);

  useSessionResumeGuard({
    verify: verifySession,
    onInvalid: invalidateSession,
  });

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!auth0.isLoading && !auth0.isAuthenticated) {
      auth0.loginWithRedirect();
    }
    if (
      !hasRendered.current &&
      ref?.current &&
      dependencies &&
      !auth0.isLoading &&
      auth0.isAuthenticated
    ) {
      hasRendered.current = true;
      console.log("Dependencies loaded, rendering mfe-dev-kit application...");
      render({ dependencies, root: ref.current });
    }
  }, [
    auth0.isLoading,
    auth0.isAuthenticated,
    auth0.loginWithRedirect,
    dependencies,
  ]);

  return (
    <ContainerLayout
      isLoggedIn={auth0.isAuthenticated}
      isEmbeddedLayout={isEmbeddedLayout}
      onLoginButtonClick={() => {
        if (auth0.isAuthenticated) {
          auth0.logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          });
        } else {
          auth0.loginWithRedirect();
        }
      }}
      currentLocale={dependencies?.locale as LocaleCodeType}
      onLanguageChange={(locale) =>
        dependencies && setDependencies({ ...dependencies, locale })
      }
    >
      {window.location.pathname.includes("cashier-app") ? (
        <div className="ps-tw-reset">
          <div ref={ref}>
            <div style={{ height: "100vh" }}>
              <LoadingScreen description={"Loading dependencies..."} />
            </div>
          </div>
        </div>
      ) : (
        <ContainerLogin />
      )}
    </ContainerLayout>
  );
};
